import axios from 'axios';
import defaultSecurityAxios, {
  axiosBuyer,
  axiosBuyerPassthrough,
  axiosSecurity,
  axiosSecurityPassthrough,
  axiosOneId
} from '../../hooks/axios';
import { getClientEnvironmentDetails } from './client-environment-details.util';
import { elfLogger } from './elf-logger.service';
import { installElfGlobalErrorHandlers } from './elf-error-handler';
import { installElfAxiosLogging } from './elf-http';

let initialized = false;

/**
 * Bootstraps ELF client logging for the buyer onboarding web app. Safe to call
 * multiple times. Mirrors one-ap-customer_web's ElfLoggingModule constructor:
 * wires the global error handlers, installs HTTP logging on the shared axios
 * instances, and emits the client environment once.
 */
export const initElfLogging = (): void => {
  if (initialized) {
    return;
  }
  initialized = true;

  installElfGlobalErrorHandlers();
  installAxiosLogging();
  logClientEnvironment();
};

/**
 * Attach ELF HTTP logging to the shared axios instances. App.tsx uses the raw
 * default `axios`, while the hooks use the named instances created in
 * src/hooks/axios.js. installElfAxiosLogging is idempotent, so each instance is
 * only instrumented once. Instrumenting here (a .ts module) keeps src/hooks/axios.js
 * free of relative imports, which are disallowed from `.js` files under the
 * app's `"type": "module"` (strict-ESM) setting.
 */
const installAxiosLogging = (): void => {
  [
    axios,
    defaultSecurityAxios,
    axiosBuyer,
    axiosBuyerPassthrough,
    axiosSecurity,
    axiosSecurityPassthrough,
    axiosOneId
  ].forEach((instance) => installElfAxiosLogging(instance));
};

/**
 * Emit the client environment once at bootstrap, mirroring the
 * one-ap-unauthenticated-landing "Details of Client Environment" log.
 */
const logClientEnvironment = (): void => {
  try {
    const pageUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
    elfLogger.info('Details of Client Environment', {
      pageUrl,
      ...getClientEnvironmentDetails()
    });
  } catch {
    // Logging must never break bootstrap.
  }
};
